'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { ShoppingCartIcon, Trash2Icon, CreditCardIcon, LogInIcon, InfoIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LazyImage, NoImagePlaceholder } from '@/components/partials';
import { useLocalCartStore, useLoginDialogStore } from '@/store';
import { getResizedPictureUrl } from '@/lib/utils';
import { useTranslatedRoute } from '@/hooks';
import GuestCheckoutDialog from './guest-checkout-dialog';
const GuestCartContent = () => {
  const $ = _c(113);
  const t = useTranslations();
  const {
    items,
    updateItem,
    removeItem,
    clearItems,
    hydrated
  } = useLocalCartStore();
  const {
    open: openLoginDialog
  } = useLoginDialogStore();
  const {
    buildRoute
  } = useTranslatedRoute();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState(null);
  const totalQuantity = items.reduce(_temp, 0);
  let t0;
  if ($[0] !== deleteProductId || $[1] !== items) {
    t0 = deleteProductId ? items.find(i_0 => i_0.productId === deleteProductId) : null;
    $[0] = deleteProductId;
    $[1] = items;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const selectedItem = t0;
  let t1;
  let t2;
  if ($[3] !== checkoutOpen || $[4] !== items.length || $[5] !== pathname || $[6] !== router || $[7] !== searchParams) {
    t1 = () => {
      if (searchParams.get("checkout") === "1" && items.length > 0 && !checkoutOpen) {
        setCheckoutOpen(true);
        router.replace(pathname, {
          scroll: false
        });
      }
    };
    t2 = [searchParams, items.length, checkoutOpen, router, pathname];
    $[3] = checkoutOpen;
    $[4] = items.length;
    $[5] = pathname;
    $[6] = router;
    $[7] = searchParams;
    $[8] = t1;
    $[9] = t2;
  } else {
    t1 = $[8];
    t2 = $[9];
  }
  useEffect(t1, t2);
  const t3 = "flex w-full flex-col gap-6 rounded-lg bg-white p-6 shadow-sm";
  const t4 = "flex items-center justify-between gap-3";
  const t5 = "text-base font-semibold text-gray-900";
  let t6;
  if ($[10] !== t) {
    t6 = t("shopping_cart");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = items.length > 0 && <span className="text-muted-foreground ml-2 text-sm font-normal">{t("cart_summary", {
      items: items.length,
      quantity: totalQuantity
    })}</span>;
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <h2 className={t5}>{t6}{t7}</h2>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== items.length || $[16] !== t) {
    t9 = items.length > 0 && <Button variant="outline" className="text-destructive border-destructive/30 hover:border-destructive hover:text-destructive text-xs" onClick={() => setClearOpen(true)}><Trash2Icon className="mr-1 h-3.5 w-3.5" />{t("clear_cart")}</Button>;
    $[15] = items.length;
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <div className={t4}>{t8}{t9}</div>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" />;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t) {
    t12 = t("guest_cart_info");
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12) {
    t13 = <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{t11}<span>{t12}</span></div>;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Separator />;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== hydrated) {
    t15 = !hydrated && <div className="flex flex-col gap-3">{[1, 2, 3].map(_temp2)}</div>;
    $[27] = hydrated;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== hydrated || $[30] !== items.length || $[31] !== t) {
    t16 = hydrated && items.length === 0 && <div className="flex flex-col items-center gap-3 py-12 text-center"><ShoppingCartIcon className="text-muted-foreground h-12 w-12" /><p className="text-muted-foreground text-sm">{t("guest_cart_empty")}</p></div>;
    $[29] = hydrated;
    $[30] = items.length;
    $[31] = t;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== buildRoute || $[34] !== hydrated || $[35] !== items || $[36] !== openLoginDialog || $[37] !== t || $[38] !== updateItem) {
    t17 = hydrated && items.length > 0 && <div className="flex flex-col gap-3">{items.map(item => {
        const productHref = item.productSlug ? buildRoute("product", ...(item.categorySlugs ?? []), item.productSlug) : buildRoute("products");
        return <div key={item.productId} className="flex flex-col gap-3 rounded-lg border bg-white p-4 shadow-sm"><div className="flex items-start gap-4"><Link href={productHref} className="relative block aspect-4/3 w-20 shrink-0 overflow-hidden rounded">{item.mainPicturePath ? <LazyImage src={getResizedPictureUrl(item.mainPicturePath, 200, 150)} alt={item.productTitle} fill={true} sizes="80px" className="object-cover" /> : <div className="bg-muted flex h-full w-full items-center justify-center"><NoImagePlaceholder iconSize={5} /></div>}</Link><Link href={productHref} className="flex flex-1 flex-col gap-1"><span className="font-semibold text-gray-900">{item.productTitle}</span><p className="text-muted-foreground text-xs">{item.productCode}</p></Link><div className="hidden shrink-0 items-center gap-2 sm:flex"><NumberInput value={item.quantity} onValueChange={value => updateItem(item.productId, value ?? 1)} min={1} max={item.stock} stepper={1} className="w-28!" /><Button size="icon" variant="outline" className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive shrink-0" onClick={() => setDeleteProductId(item.productId)}><Trash2Icon className="h-4 w-4" /></Button></div></div><div className="flex items-center justify-end gap-2 sm:hidden"><div className="w-28 shrink-0"><NumberInput value={item.quantity} onValueChange={value_0 => updateItem(item.productId, value_0 ?? 1)} min={1} max={item.stock} stepper={1} /></div><Button size="icon" variant="outline" className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive shrink-0" onClick={() => setDeleteProductId(item.productId)}><Trash2Icon className="h-4 w-4" /></Button></div></div>;
      })}<Separator /><div className="flex flex-col gap-2 sm:flex-row sm:justify-end"><Button variant="outline" className="gap-2" onClick={openLoginDialog}><LogInIcon className="h-4 w-4" />{t("login_to_use_cart")}</Button><Button className="gap-2 bg-green-600 text-white hover:bg-green-700" onClick={() => setCheckoutOpen(true)}><CreditCardIcon className="h-4 w-4" />{t("checkout_as_guest")}</Button></div></div>;
    $[33] = buildRoute;
    $[34] = hydrated;
    $[35] = items;
    $[36] = openLoginDialog;
    $[37] = t;
    $[38] = updateItem;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const t18 = !!deleteProductId;
  let t19;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = open => !open && setDeleteProductId(null);
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t) {
    t20 = t("remove");
    $[41] = t;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t20) {
    t21 = <DialogHeader><DialogTitle>{t20}</DialogTitle></DialogHeader>;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== selectedItem) {
    t22 = selectedItem && <p className="text-muted-foreground text-sm">[{selectedItem.productCode}] {selectedItem.productTitle} ×{selectedItem.quantity}</p>;
    $[45] = selectedItem;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t22) {
    t23 = <ScrollArea className="min-h-0 flex-1" viewportClassName="max-h-[calc(90vh-9rem)]"><div className="px-5 pr-3 pb-4">{t22}</div></ScrollArea>;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setDeleteProductId(null);
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t) {
    t25 = t("cancel");
    $[50] = t;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t25) {
    t26 = <Button variant="outline" onClick={t24}>{t25}</Button>;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== deleteProductId || $[55] !== removeItem) {
    t27 = () => {
      if (deleteProductId) {
        removeItem(deleteProductId);
      }
      setDeleteProductId(null);
    };
    $[54] = deleteProductId;
    $[55] = removeItem;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== t) {
    t28 = t("remove");
    $[57] = t;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== t27 || $[60] !== t28) {
    t29 = <Button variant="destructive" onClick={t27}>{t28}</Button>;
    $[59] = t27;
    $[60] = t28;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== t26 || $[63] !== t29) {
    t30 = <DialogFooter className="gap-2">{t26}{t29}</DialogFooter>;
    $[62] = t26;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== t21 || $[66] !== t23 || $[67] !== t30) {
    t31 = <DialogContent aria-describedby={undefined} className="sm:max-w-sm">{t21}{t23}{t30}</DialogContent>;
    $[65] = t21;
    $[66] = t23;
    $[67] = t30;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== t18 || $[70] !== t31) {
    t32 = <Dialog open={t18} onOpenChange={t19}>{t31}</Dialog>;
    $[69] = t18;
    $[70] = t31;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== t) {
    t33 = t("clear_cart");
    $[72] = t;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t33) {
    t34 = <DialogHeader><DialogTitle>{t33}</DialogTitle></DialogHeader>;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== t) {
    t35 = t("confirm_clear_cart");
    $[76] = t;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] !== t35) {
    t36 = <ScrollArea className="min-h-0 flex-1" viewportClassName="max-h-[calc(90vh-9rem)]"><div className="px-5 pr-3 pb-4"><p className="text-muted-foreground text-sm">{t35}</p></div></ScrollArea>;
    $[78] = t35;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = () => setClearOpen(false);
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== t) {
    t38 = t("cancel");
    $[81] = t;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  let t39;
  if ($[83] !== t38) {
    t39 = <Button variant="outline" onClick={t37}>{t38}</Button>;
    $[83] = t38;
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  let t40;
  if ($[85] !== clearItems) {
    t40 = () => {
      clearItems();
      setClearOpen(false);
    };
    $[85] = clearItems;
    $[86] = t40;
  } else {
    t40 = $[86];
  }
  let t41;
  if ($[87] !== t) {
    t41 = t("clear_cart");
    $[87] = t;
    $[88] = t41;
  } else {
    t41 = $[88];
  }
  let t42;
  if ($[89] !== t40 || $[90] !== t41) {
    t42 = <Button variant="destructive" onClick={t40}>{t41}</Button>;
    $[89] = t40;
    $[90] = t41;
    $[91] = t42;
  } else {
    t42 = $[91];
  }
  let t43;
  if ($[92] !== t39 || $[93] !== t42) {
    t43 = <DialogFooter className="gap-2">{t39}{t42}</DialogFooter>;
    $[92] = t39;
    $[93] = t42;
    $[94] = t43;
  } else {
    t43 = $[94];
  }
  let t44;
  if ($[95] !== t34 || $[96] !== t36 || $[97] !== t43) {
    t44 = <DialogContent aria-describedby={undefined} className="sm:max-w-sm">{t34}{t36}{t43}</DialogContent>;
    $[95] = t34;
    $[96] = t36;
    $[97] = t43;
    $[98] = t44;
  } else {
    t44 = $[98];
  }
  let t45;
  if ($[99] !== clearOpen || $[100] !== t44) {
    t45 = <Dialog open={clearOpen} onOpenChange={setClearOpen}>{t44}</Dialog>;
    $[99] = clearOpen;
    $[100] = t44;
    $[101] = t45;
  } else {
    t45 = $[101];
  }
  let t46;
  if ($[102] !== checkoutOpen) {
    t46 = <GuestCheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} />;
    $[102] = checkoutOpen;
    $[103] = t46;
  } else {
    t46 = $[103];
  }
  let t47;
  if ($[104] !== t10 || $[105] !== t13 || $[106] !== t15 || $[107] !== t16 || $[108] !== t17 || $[109] !== t32 || $[110] !== t45 || $[111] !== t46) {
    t47 = <div className={t3}>{t10}{t13}{t14}{t15}{t16}{t17}{t32}{t45}{t46}</div>;
    $[104] = t10;
    $[105] = t13;
    $[106] = t15;
    $[107] = t16;
    $[108] = t17;
    $[109] = t32;
    $[110] = t45;
    $[111] = t46;
    $[112] = t47;
  } else {
    t47 = $[112];
  }
  return t47;
};
export default GuestCartContent;
function _temp(acc, i) {
  return acc + i.quantity;
}
function _temp2(i_1) {
  return <div key={i_1} className="flex items-start gap-4 rounded-lg border bg-white p-4 shadow-sm"><Skeleton className="h-15 w-20 rounded" /><div className="flex-1 space-y-2"><Skeleton className="h-3 w-16" /><Skeleton className="h-4 w-38" /></div><div className="flex items-center gap-2"><Skeleton className="h-9 w-28" /><Skeleton className="h-9 w-9 rounded" /></div></div>;
}